/**
 * Content Authority — Accrezeo-OS UI copy
 *
 * Every English string longer than five words lives here. Components import
 * from this file. Inline English in JSX (beyond short labels) is a violation
 * caught by hardcode_scan's frontend copy rule.
 *
 * Shape: copy.<surface>.<area>.<element> = string | function.
 *
 * Authority: docs/contracts/DESIGN_IMPLEMENTATION_CONTRACT.md Section 5.
 * Peer: backend/core/ai/routing_config.py (coceo_voice caller).
 */

/* ------------------------------------------------------------------ */
/* Co-CEO voice templates                                              */
/*                                                                    */
/* Each template takes a data object and returns { fallback, fetch }. */
/* fallback: a deterministic same-register string ready to render.     */
/* fetch:    an async function that calls /api/coceo-voice and returns */
/*           an LLM-rewritten line in the same register, or throws on  */
/*           timeout / failure so the caller can fall back cleanly.    */
/*                                                                    */
/* The caller pattern (see components/shared/CoCeoVoiceBlock.jsx):     */
/*   const { fallback, fetch } = copy.inbox.header.coceoVoiceTemplate  */
/*     (data);                                                        */
/*   setLine(fallback);                                               */
/*   fetch().then(setLine).catch(() => {});                           */
/* ------------------------------------------------------------------ */

const COCEO_ENDPOINT = '/api/coceo-voice';
const COCEO_TIMEOUT_MS = 1500;

async function fetchCoCeoLine(surface, data) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), COCEO_TIMEOUT_MS);
  try {
    const res = await fetch(COCEO_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ surface, data }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`coceo_voice_http_${res.status}`);
    const body = await res.json();
    if (!body || typeof body.line !== 'string' || !body.line.trim()) {
      throw new Error('coceo_voice_empty');
    }
    return body.line;
  } finally {
    clearTimeout(timer);
  }
}

/* ------------------------------------------------------------------ */

export const copy = {
  shared: {
    regenerating: 'analyzing with new parameters',
    retry: 'Retry',
    dismiss: 'Dismiss',
    loadingRoom: 'Reading the room…',
    saved: 'Saved',
    errorGeneric: 'Something went off-script. Retry or head back to the Inbox.',
  },

  sidebar: {
    groups: {
      office: 'Office',
      sourcing: 'Sourcing',
      relationships: 'Relationships',
      content: 'Content',
      analysis: 'Analysis',
      reference: 'Reference',
    },
    settings: 'Settings',
  },

  inbox: {
    coceoVoice: 'active',
    shortcuts: {
      triage: 'J / K — move between items',
      commit: 'Enter — commit the highlighted action',
      search: '/ — search across the Inbox',
    },
    header: {
      title: 'Morning cockpit',
      coceoVoiceLoadingFallback: 'Reading the room…',
      coceoVoiceTemplate: (d = {}) => {
        const strong = Number(d.strongCount ?? 0);
        const weak = Number(d.weakCount ?? 0);
        const oldestAge = Number(d.oldestAgeHours ?? 0);
        const nextTitle = d.nextObligationTitle || 'the next decision';
        const nextWhen = d.nextObligationTimeIST || 'later today';
        const fallback = `You have ${strong} strong and ${weak} weak items to triage this morning. The oldest has been waiting ${oldestAge} hours. ${nextTitle} is on you next — ${nextWhen}.`;
        return { fallback, fetch: () => fetchCoCeoLine('inbox', d) };
      },
    },
    summary: {
      strong: 'Strong',
      weak: 'Weak',
      pass: 'Pass',
      directional: 'Directional',
      oldest: 'Oldest',
      overnight: 'Overnight',
      kbWrites: 'KB writes',
      deltaVsYesterday: 'vs yesterday',
    },
    filters: {
      all: 'All agents',
      priority: 'Priority',
      sector: 'Sector',
      age: 'Age',
    },
    actions: {
      accept: 'Accept',
      defer: 'Defer',
      reject: 'Pass',
      retriage: 'Re-triage this batch',
    },
    empty: {
      title: 'Inbox is clear',
      body: 'Nothing is waiting on you right now. Head to Strategy Insights to read the week.',
    },
    errors: {
      summaryFailed: 'The summary did not load. Retry or check the Inbox directly.',
      listFailed: 'Could not load the Inbox. Check connection and retry.',
    },
  },

  strategyInsights: {
    coceoVoice: 'active',
    shortcuts: {
      sections: '1 / 2 / 3 / 4 — jump between sections',
      resynth: 'R — re-synthesize the active section',
    },
    intro: {
      title: 'Strategy Insights',
      coceoVoiceLoadingFallback: 'Gathering this week’s patterns…',
      coceoVoiceTemplate: (d = {}) => {
        const patterns = Number(d.patternCount ?? 0);
        const softening = Number(d.softeningCount ?? 0);
        const topSector = d.topSector || 'your top sector';
        const fallback = `Three patterns tightened this week and ${softening} loosened. ${topSector} kept showing up. Worth a slower read before the week gets loud.`;
        return { fallback, fetch: () => fetchCoCeoLine('strategyInsights', d) };
      },
    },
    sections: {
      patterns: 'Pattern Observations',
      temperature: 'Sector Temperature',
      convictionChanges: 'Conviction Changes This Week',
      thesisReads: 'Thesis-Relevant Reads',
    },
    actions: {
      resynth: 'Re-synthesize',
      openSource: 'Open source',
    },
    empty: {
      patterns: 'No new patterns this week.',
      temperature: 'Sector temperature is flat this week.',
      convictionChanges: 'No conviction changes this week.',
      thesisReads: 'No thesis-relevant reads this week.',
    },
    errors: {
      syntheseFailed: 'Synthesis failed — the old version is still shown below. Retry to refresh.',
    },
  },

  dealEvaluator: {
    coceoVoice: 'silent',
    shortcuts: {
      expand: 'Enter — expand the highlighted parameter',
      rerun: 'R — re-run analysis with current knobs',
    },
    fca: {
      directionalTitle: 'Directional signal',
      paramsTitle: 'Ten parameters',
      knobsTitle: 'Analysis knobs',
      knobLabels: {
        scrutiny: 'Scrutiny',
        researchDepth: 'Research Depth',
        indiaLens: 'India Lens',
      },
      knobOptions: {
        scrutiny: { fair: 'Fair', strict: 'Strict', lean: 'Lean' },
        researchDepth: { quick: 'Quick', deep: 'Deep' },
        indiaLens: { default: 'Default', strong: 'Strong' },
      },
    },
    actions: {
      accept: 'Accept — worth a call',
      defer: 'Defer',
      reject: 'Pass',
      export: 'Export PDF',
      rerun: 'Re-run with these knobs',
    },
    regenerating: 'Re-running with new parameters',
    empty: {
      title: 'No deals in the evaluator',
      body: 'Upload a deck on the Deal Sourcing page to begin.',
    },
    errors: {
      rerunFailed: 'Re-run did not complete. The previous version is still shown — retry or check the pipeline.',
    },
  },

  postStudio: {
    coceoVoice: 'silent',
    shortcuts: {
      tabs: '1 / 2 / 3 — switch council tab',
      redraft: 'D — redraft this thesis',
    },
    tabs: {
      firstPerson: 'First-person',
      provocation: 'Provocation',
      counterData: 'Counter-data',
    },
    actions: {
      publish: 'Publish',
      save: 'Save draft',
      redraft: 'Redraft',
      reject: 'Reject draft',
      copy: 'Copy text',
    },
    indicators: {
      thesisStrength: 'Thesis link strength',
      qualityGate: 'Quality gate',
      kbSources: 'KB sources cited',
    },
    councilSteps: {
      firstPerson: 'First-person',
      provocation: 'Provocation',
      counterData: 'Counter-data',
      qualityGate: 'Quality gate',
    },
    regenerating: 'Redrafting with current council',
    empty: {
      title: 'No drafts in the studio',
      body: 'The LinkedIn Post agent surfaces drafts as the council produces them.',
    },
    errors: {
      redraftFailed: 'Redraft did not complete. The previous version is still shown — retry to refresh.',
    },
  },

  kb: {
    coceoVoice: 'silent',
    shortcuts: {
      search: '/ — search knowledge base',
      conviction: 'C — open conviction popover',
      reExamine: 'E — re-examine this thesis',
    },
    header: {
      title: 'Knowledge Base',
      searchPlaceholder: 'Search theses, sectors, signals…',
    },
    sections: {
      accrezeoAnalysis: 'Accrezeo Analysis',
      counterArgument: 'Counter Argument',
      crossSectorParallels: 'Cross-Sector Parallels',
      relatedEntries: 'Related Entries',
      signalStrength: 'Signal Strength',
      ceoDecision: 'CEO Decision',
    },
    conviction: {
      labels: {
        hardening: 'Hardening',
        stable: 'Stable',
        softening: 'Softening',
      },
      editPrompt: 'What changed? Write one sentence.',
      editCommit: 'Commit change',
      historyTitle: 'Conviction history',
      reExamine: 'Re-examine this thesis',
    },
    actions: {
      openSource: 'Open original source',
      copyLink: 'Copy link',
      archive: 'Archive entry',
    },
    regenerating: 'Re-examining with fresh context',
    empty: {
      title: 'Knowledge Base is empty',
      body: 'Decisions made in the Inbox seed this pool. Accept one to begin.',
    },
    errors: {
      convictionPatchFailed: 'Conviction did not save. The previous state is still shown — retry or check the Inbox.',
      reExamineFailed: 'Re-examination did not complete. The previous analysis is still shown — retry.',
    },
  },
};

export default copy;
